import React, { useEffect, useState, useRef } from "react";

import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  List,
  Paper,
  Button,
  IconButton,
  CircularProgress,
  Divider,
  Avatar,
  Snackbar,
  Alert,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import {
  DragDropContext,
  Droppable,
  Draggable,
} from "react-beautiful-dnd";

import { useSelector } from "react-redux";

function ReOrderCategory({ open, onClose, categories = [], refresh }) {
  const userState = useSelector((state) => state.user);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [orderedCategories, setOrderedCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    action: null,
    severity: "success",
  });

  const lastState = useRef([]);
  const initialState = useRef([]); // 🔥 RESET FIX
  const scrollContainer = useRef(null);
  const autoSaveTimer = useRef(null);

  // ================= IOS LOCK =================
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [open]);

  // ================= INIT =================
  useEffect(() => {
    if (!categories?.length) return;

    const sorted = [...categories].sort(
      (a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)
    );

    setOrderedCategories(sorted);

    // 🔥 snapshot initial (important)
    initialState.current = structuredClone(sorted);
  }, [categories, open]);

  // ================= HAPTIC =================
  const vibrate = () => {
    if (navigator.vibrate) navigator.vibrate(12);
  };

  // ================= AUTO SCROLL (FIX ACTIVE) =================
  const handleAutoScroll = (clientY) => {
    const container = scrollContainer.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const offset = 90;

    if (clientY < rect.top + offset) {
      container.scrollBy({ top: -25, behavior: "smooth" });
    }

    if (clientY > rect.bottom - offset) {
      container.scrollBy({ top: 25, behavior: "smooth" });
    }
  };

  // ================= UNDO LAST =================
  const handleUndo = () => {
    if (!lastState.current.length) return;
    setOrderedCategories(lastState.current);
    setSnack((s) => ({ ...s, open: false }));
  };

  // ================= RESET FULL =================
  const handleReset = () => {
    setOrderedCategories(structuredClone(initialState.current));

    setSnack({
      open: true,
      message: "Reset à l’état initial",
      severity: "info",
      action: null,
    });
  };

  // ================= DRAG =================
  const handleDragStart = () => {
    vibrate();
    lastState.current = structuredClone(orderedCategories);
  };

  const handleDragUpdate = (provided, snapshot) => {
    if (snapshot?.clientY) {
      handleAutoScroll(snapshot.clientY);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(orderedCategories);

    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setOrderedCategories(items);

    setSnack({
      open: true,
      message: "Ordre modifié",
      severity: "info",
      action: true,
    });
  };

  // ================= SAVE =================
  const handleSave = async (list = orderedCategories) => {
    const backup = [...orderedCategories];

    try {
      setSaving(true);

      setOrderedCategories(list);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Recipe/UpdateOrderCategories`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify(
            list.map((c, i) => ({
              id: c.id,
              ordre: i + 1,
            }))
          ),
        }
      );

      if (!res.ok) throw new Error("API failed");

      await refresh?.(true);
      onClose();
    } catch (err) {
      console.error(err);

      setOrderedCategories(backup);

      setSnack({
        open: true,
        message: "Erreur sauvegarde - rollback",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const getLabel = (cat) =>
    userState.lang === "FR" ? cat.textFr : cat.textEn;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 4,
            height: fullScreen ? "100%" : "85vh",
            overflow: "hidden",
          },
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg,#1976d2,#42a5f5)",
            color: "white",
            position: "relative",
          }}
        >
          <IconButton onClick={onClose}
            sx={{ position: "absolute", right: 10, top: 10, color: "white" }}
          >
            <CloseIcon />
          </IconButton>

          <Typography fontWeight={700}>
            Réorganiser catégories
          </Typography>

          <Typography fontSize={12} sx={{ opacity: 0.85 }}>
            Drag & drop pour changer l’ordre
          </Typography>
        </Box>

        {/* ACTION BAR */}
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 2, py: 1 }}>
          <Button
            size="small"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
          >
            Reset
          </Button>

          <Typography fontSize={12} color="text.secondary">
            Drag actif
          </Typography>
        </Box>

        {/* CONTENT */}
        <DialogContent
          ref={scrollContainer}
          sx={{ p: 2, background: "#f4f6f8", overflowY: "auto" }}
        >
          <DragDropContext
            onDragStart={handleDragStart}
            onDragUpdate={handleDragUpdate}
            onDragEnd={handleDragEnd}
          >
            <Droppable droppableId="list">
              {(provided) => (
                <List ref={provided.innerRef} {...provided.droppableProps}>
                  {orderedCategories.map((cat, index) => (
                    <Draggable
                      key={cat.id}
                      draggableId={cat.id.toString()}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          sx={{
                            mb: 1.5,
                            display: "flex",
                            alignItems: "center",
                            p: 1.5,
                            borderRadius: 3,
                            cursor: "grab",
                            transition: "0.2s",
                            transform: snapshot.isDragging
                              ? "scale(1.05)"
                              : "scale(1)",
                            boxShadow: snapshot.isDragging
                              ? "0 25px 60px rgba(0,0,0,0.3)"
                              : "0 3px 10px rgba(0,0,0,0.08)",
                          }}
                        >
                          <Avatar src={cat.imageUrl} sx={{ mr: 2 }} />

                          <Typography sx={{ flex: 1, fontWeight: 600 }}>
                            {getLabel(cat)}
                          </Typography>

                          <DragIndicatorIcon sx={{ color: "#aaa" }} />
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </DialogContent>

        {/* FOOTER */}
        <Divider />

        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={handleUndo} startIcon={<UndoIcon />}>
            Undo
          </Button>

          <Button
            onClick={() => handleSave()}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          >
            Save
          </Button>
        </Box>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ReOrderCategory;